import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ImportstepsModule } from '@app/features/importsteps/importsteps.module';
import { BackgroundDirective } from '@app/shared/directives/background/background.directive';
import { DisabledControlDirective } from '@app/shared/directives/form-control/disabled-control.directive';
import { QaDirective } from '@app/shared/directives/qa/qa.directive';
import { BackendSessionDirective } from '@app/shared/directives/session/backend-session.directive';
import { CurrencySymbolPipe } from '@app/shared/pipes/currency-symbol.pipe';
import { FilesizePipe } from '@app/shared/pipes/filesize.pipe';
import { ButtonComponent } from '@components/buttons/button/button.component';
import { DataTableComponent } from '@components/data/data-table/data-table.component';
import { DetailFormComponent } from '@components/data/detail-form/detail-form.component';
import { CheckboxComponent } from '@components/form-controls/checkbox/checkbox.component';
import { SlideToggleComponent } from '@components/form-controls/slide-toggle/slide-toggle.component';
import { ProgressBarComponent } from '@components/indicators/progress-bar/progress-bar.component';
import { SpinnerComponent } from '@components/indicators/spinner/spinner.component';
import { ConfirmDialogComponent } from '@components/modals/confirm-dialog/confirm-dialog.component';
import { ImportDataDialogComponent } from '@components/modals/import-data-dialog/import-data-dialog.component';
import { ContextMenuItemComponent } from '@components/navigation/context-menu/context-menu-item/context-menu-item.component';
import { ContextMenuComponent } from '@components/navigation/context-menu/context-menu.component';
import { SnackbarComponent } from '@components/overlays/snackbar/snackbar.component';
import { TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { IsEllipsisActiveDirective } from '../directives/font/is-ellipsis-active.directive';
import { TruncateDirective } from '../directives/font/truncate.directive';
import { DecimalDirective } from '../directives/form-control/decimal.directive';
import { NumberDirective } from '../directives/form-control/number.directive';
import { PercentageDirective } from '../directives/form-control/percentage.directive';
import { NgVarDirective } from '../directives/ngVar.directive';
import { PreventDoubleClickDirective } from '../directives/prevent.doubleClick.directive';
import { SubmitDirective } from '../directives/submit.directive';
import { MaterialModule } from '../material.module';
import { AggregationPipe } from '../pipes/aggregation.pipe';
import { VfsCurrencyPipe } from '../pipes/currency.pipe';
import { UTCDateNoTime, UTCDatePipe, UTCSecondsToTime, UTCShortDatePipe } from '../pipes/utcdate';
import { ButtonIconComponent } from './buttons/button-icon/button-icon.component';
import { ButtonTabsNavComponent } from './buttons/button-tabs-nav/button-tabs-nav.component';
import { ButtonToggleGroupComponent } from './buttons/button-toggle-group/button-toggle-group.component';
import { ButtonToggleTabsComponent } from './buttons/button-toggle-tabs/button-toggle-tabs.component';
import { SpeedDialComponent } from './buttons/speed-dial/speed-dial.component';
import { AuditInfoComponent } from './data/audit-info/audit-info.component';
import { DataTableNewComponent } from './data/data-table-new/data-table-new.component';
import { DatatableActionsDirective } from './data/data-table-new/directives/datatable-actions.directive';
import { DatatableAdvancedFilterDirective } from './data/data-table-new/directives/datatable-advanced-filter.directive';
import { DatatableFilterDirective } from './data/data-table-new/directives/datatable-filter.directive';
import { DatatableInfiniteScrollDirective } from './data/data-table-new/directives/datatable-infinite-scroll.directive';
import { DatatableMultiselectDirective } from './data/data-table-new/directives/datatable-multiselect.directive';
import { DatatablePaginationDirective } from './data/data-table-new/directives/datatable-pagination.directive';
import { DatatableSearchDirective } from './data/data-table-new/directives/datatable-search.directive';
import { DatatableSortDirective } from './data/data-table-new/directives/datatable-sort.directive';
import { DatatableSwitchViewDirective } from './data/data-table-new/directives/datatable-switch-view.directive';
import { DependandFiltersDirective } from './data/data-table-new/directives/dependant-filters.directive';
import { FilterBuilderDirective } from './data/data-table-new/directives/filter-builder.directive';
import { ServiceImpl } from './data/data-table-new/service/service.impl';
import { StateServiceImpl } from './data/data-table-new/state/state.service.impl';
import { CardViewNewComponent } from './data/data-table-new/view/card-view-new/card-view-new.component';
import { GridViewNewComponent } from './data/data-table-new/view/grid-view-new/grid-view-new.component';
import { ListViewNewComponent } from './data/data-table-new/view/list-view-new/list-view-new.component';
import { DataTableSimpleComponent } from './data/data-table-simple/data-table-simple.component';
import { BaseViewComponent } from './data/data-table/base-view/base-view.component';
import { CardViewComponent } from './data/data-table/card-view/card-view.component';
import { GridViewComponent } from './data/data-table/grid-view/grid-view.component';
import { ListViewComponent } from './data/data-table/list-view/list-view.component';
import { TemplateCardComponent } from './data/data-table/templates/template-card/template-card.component';
import { NoTxComponent } from './data/no-tx/no-tx.component';
import { TreeComponent } from './data/tree/tree.component';
import { CheckboxGroupComponent } from './form-controls/checkbox-group/checkbox-group.component';
import { DatePickerCustomHeaderComponent } from './form-controls/datepicker/datepicker-header/datepicker-custom-header.component';
import { DatepickerComponent } from './form-controls/datepicker/datepicker.component';
import { InputNumberComponent } from './form-controls/inputs/input-number/input-number.component';
import { InputPasswordComponent } from './form-controls/inputs/input-password/input-password.component';
import { InputSearchComponent } from './form-controls/inputs/input-search/input-search.component';
import { InputStringtableComponent } from './form-controls/inputs/input-stringtable/input-stringtable.component';
import { StringtableDataComponent } from './form-controls/inputs/input-stringtable/stringtable-data/stringtable-data.component';
import { InputComponent } from './form-controls/inputs/input/input.component';
import { TextAreaComponent } from './form-controls/inputs/text-area/text-area.component';
import { RadioGroupComponent } from './form-controls/radio-group/radio-group.component';
import { SelectSearchComponent } from './form-controls/select/select-search/select-search.component';
import { SelectComponent } from './form-controls/select/select.component';
import { SettingsToggleComponent } from './form-controls/settings-toggle/settings-toggle.component';
import { SimpleSelectComponent } from './form-controls/simple-select/simple-select.component';
import { TimePickerComponent } from './form-controls/timepicker/timepicker.component';
import { AvatarDialogComponent } from './form-controls/upload/avatar-upload/avatar-dialog/avatar-dialog.component';
import { AvatarUploadComponent } from './form-controls/upload/avatar-upload/avatar-upload.component';
import { UploadComponent } from './form-controls/upload/upload/upload.component';
import { EnumerableIconComponent } from './icons/enumerable-icon/enumerable-icon.component';
import { IconSelectionComponent } from './icons/icon-selection/icon-selection.component';
import { EmptyStateComponent } from './indicators/empty-state/empty-state.component';
import { StateComponent } from './indicators/state/state.component';
import { StatusComponent } from './indicators/status/status.component';
import { TypeComponent } from './indicators/type/type.component';
import { FilterBaseComponent } from './layout/filter-base/filter-base.component';
import { StepperComponent } from './layout/stepper/stepper.component';
import { AlertDialogComponent } from './modals/alert-dialog/alert-dialog.component';
import { DialogBaseComponent } from './modals/dialog-base/dialog-base.component';
import { InfoDialogComponent } from './modals/info-dialog/info-dialog.component';
import { TranslateDialogComponent } from './modals/translate-dialog/translate-dialog.component';
import { WaitDialogComponent } from './modals/wait-dialog/wait-dialog.component';
import { ActionBarComponent } from './navigation/action-bar/action-bar.component';
import { AnchorMenuComponent } from './navigation/anchor-menu/anchor-menu.component';
import { PageGroupComponent } from './navigation/page-group/page-group.component';
import { LoadingComponent } from './overlays/loading/loading.component';
@NgModule({
  declarations: [
    DataTableComponent,
    DetailFormComponent,
    ConfirmDialogComponent,
    ImportDataDialogComponent,
    CheckboxComponent,
    SlideToggleComponent,
    ContextMenuComponent,
    ContextMenuItemComponent,
    SpinnerComponent,
    ButtonComponent,
    ButtonToggleGroupComponent,
    ProgressBarComponent,
    AlertDialogComponent,
    RadioGroupComponent,
    SelectComponent,
    SelectSearchComponent,
    AnchorMenuComponent,
    InputComponent,
    SnackbarComponent,
    TreeComponent,
    ActionBarComponent,
    GridViewComponent,
    GridViewNewComponent,
    CardViewComponent,
    BaseViewComponent,
    DatepickerComponent,
    DatePickerCustomHeaderComponent,
    StepperComponent,
    InfoDialogComponent,
    UTCDatePipe,
    UTCShortDatePipe,
    UTCSecondsToTime,
    UTCDateNoTime,
    WaitDialogComponent,
    DialogBaseComponent,
    TimePickerComponent,
    FilterBaseComponent,
    AvatarUploadComponent,
    AvatarDialogComponent,
    SimpleSelectComponent,
    ButtonToggleTabsComponent,
    LoadingComponent,
    TextAreaComponent,
    InputNumberComponent,
    InputSearchComponent,
    InputPasswordComponent,
    ButtonIconComponent,
    EnumerableIconComponent,
    ListViewComponent,
    PageGroupComponent,
    UploadComponent,
    TemplateCardComponent,
    EmptyStateComponent,
    StatusComponent,
    QaDirective,
    DisabledControlDirective,
    PercentageDirective,
    DecimalDirective,
    BackgroundDirective,
    IconSelectionComponent,
    InputStringtableComponent,
    StringtableDataComponent,
    AuditInfoComponent,
    IsEllipsisActiveDirective,
    TruncateDirective,
    DataTableNewComponent,
    DataTableSimpleComponent,
    DatatableSortDirective,
    DatatablePaginationDirective,
    DatatableInfiniteScrollDirective,
    DatatableSearchDirective,
    DatatableFilterDirective,
    DatatableAdvancedFilterDirective,
    FilterBuilderDirective,
    DatatableMultiselectDirective,
    DatatableActionsDirective,
    DatatableSwitchViewDirective,
    GridViewNewComponent,
    CardViewNewComponent,
    ListViewNewComponent,
    TranslateDialogComponent,
    CheckboxGroupComponent,
    StateComponent,
    TypeComponent,
    ButtonTabsNavComponent,
    SpeedDialComponent,
    AggregationPipe,
    FilesizePipe,
    CurrencySymbolPipe,
    SettingsToggleComponent,
    NoTxComponent,
    NgVarDirective,
    DependandFiltersDirective,
    PreventDoubleClickDirective,
    BackendSessionDirective,
    SubmitDirective,
    VfsCurrencyPipe,
    NumberDirective
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslateModule,
    InfiniteScrollModule,
    ImportstepsModule
  ],
  exports: [
    DataTableComponent,
    DetailFormComponent,
    ConfirmDialogComponent,
    ImportDataDialogComponent,
    CheckboxComponent,
    SlideToggleComponent,
    ContextMenuComponent,
    ContextMenuItemComponent,
    SpinnerComponent,
    ButtonComponent,
    ButtonToggleGroupComponent,
    ProgressBarComponent,
    AlertDialogComponent,
    RadioGroupComponent,
    SelectComponent,
    SelectSearchComponent,
    AnchorMenuComponent,
    InputComponent,
    SnackbarComponent,
    TreeComponent,
    ActionBarComponent,
    DatepickerComponent,
    DatePickerCustomHeaderComponent,
    StepperComponent,
    InfoDialogComponent,
    UTCDatePipe,
    UTCShortDatePipe,
    UTCSecondsToTime,
    UTCDateNoTime,
    WaitDialogComponent,
    DialogBaseComponent,
    TimePickerComponent,
    FilterBaseComponent,
    AvatarUploadComponent,
    AvatarDialogComponent,
    SimpleSelectComponent,
    ButtonToggleTabsComponent,
    LoadingComponent,
    TextAreaComponent,
    InputNumberComponent,
    InputSearchComponent,
    InputPasswordComponent,
    ButtonIconComponent,
    EnumerableIconComponent,
    PageGroupComponent,
    UploadComponent,
    TemplateCardComponent,
    EmptyStateComponent,
    StatusComponent,
    QaDirective,
    BackgroundDirective,
    DisabledControlDirective,
    PercentageDirective,
    DecimalDirective,
    InputStringtableComponent,
    StringtableDataComponent,
    AuditInfoComponent,
    IsEllipsisActiveDirective,
    TruncateDirective,
    DataTableNewComponent,
    DataTableSimpleComponent,
    DatatableSortDirective,
    DatatablePaginationDirective,
    DatatableInfiniteScrollDirective,
    DatatableSearchDirective,
    DatatableFilterDirective,
    DatatableAdvancedFilterDirective,
    FilterBuilderDirective,
    DatatableMultiselectDirective,
    DatatableActionsDirective,
    DatatableSwitchViewDirective,
    TranslateDialogComponent,
    CheckboxGroupComponent,
    StateComponent,
    TypeComponent,
    ButtonTabsNavComponent,
    SpeedDialComponent,
    AggregationPipe,
    FilesizePipe,
    CurrencySymbolPipe,
    SettingsToggleComponent,
    NoTxComponent,
    NgVarDirective,
    DependandFiltersDirective,
    PreventDoubleClickDirective,
    BackendSessionDirective,
    SubmitDirective,
    VfsCurrencyPipe,
    NumberDirective
  ],
  providers: [
    UTCDatePipe,
    CurrencySymbolPipe,
    VfsCurrencyPipe,
    {
      provide: 'EntityService',
      useClass: ServiceImpl
    },
    {
      provide: 'StateService',
      useClass: StateServiceImpl
    }
  ]
})
export class ComponentsModule {}
